var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import styled, { css } from "styled-components";
import { hexToRgb } from "./colors";
const getFontSize = (buttonSize, isSmallDevice) => {
    if (isSmallDevice) {
        // 15px for all sizes
        return "15px";
    }
    switch (buttonSize) {
        case "small":
            return "15px";
        case "medium":
            return "15px";
        case "large":
            return "20px";
    }
};
const getSidePadding = (buttonSize, isSmallDevice) => {
    if (isSmallDevice) {
        switch (buttonSize) {
            case "small":
            case "medium":
                return "16px";
            case "large":
                return "24px";
        }
    }
    switch (buttonSize) {
        case "small":
            return "16px";
        case "medium":
        case "large":
            return "24px";
    }
};
const getButtonHeight = (buttonSize, isSmallDevice) => {
    if (isSmallDevice) {
        switch (buttonSize) {
            case "small":
                return "40px";
            case "medium":
                return "48px";
            case "large":
                return "56px";
        }
    }
    switch (buttonSize) {
        case "small":
            return "40px";
        case "medium":
            return "56px";
        case "large":
            return "72px";
    }
};
/**
 * Primary and secondary buttons for all button uses.
 * */
const getMainColor = (variant, colors) => {
    if (variant === "neutral") {
        return "#999";
    }
    return colors[variant].main.color;
};
const getContrastColor = (variant, colors) => {
    if (variant === "neutral") {
        return "white";
    }
    return colors[variant].main.contrastColor;
};
const getHoverColor = (variant, colors) => {
    if (variant === "neutral") {
        return "#9f9f9f";
    }
    return colors[variant].main.hoverColor;
};
const getDisabledColor = (props) => {
    if (props.variant === "neutral") {
        return "blue";
    }
    const theme = props.theme;
    const color = hexToRgb(theme.colors[props.variant].main.color);
    if (!color) {
        return "#ccc";
    }
    return `rgba(${color.r}, ${color.g}, ${color.b}, 0.4)`;
};
const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) => !["variant", "size", "inverted", "fullWidth"].includes(prop),
}) `
  ${css `
    background-color: ${(props) => props.inverted
    ? "transparent"
    : getMainColor(props.variant, props.theme.colors)};
    color: ${(props) => props.inverted
    ? getMainColor(props.variant, props.theme.colors)
    : getContrastColor(props.variant, props.theme.colors)} !important;
    border-radius: 5px;

    display: inline-flex;
    align-items: center; /* vertical centering */
    justify-content: center;

    font-size: ${(props) => getFontSize(props.size, true)};
    height: ${(props) => getButtonHeight(props.size, true)};
    padding-left: ${(props) => getSidePadding(props.size, true)};
    padding-right: ${(props) => getSidePadding(props.size, true)};
    ${(props) => props.theme.MUITheme.breakpoints.up("sm")} {
      height: ${(props) => getButtonHeight(props.size, false)};
      padding-left: ${(props) => getSidePadding(props.size, false)};
      padding-right: ${(props) => getSidePadding(props.size, false)};
      font-size: ${(props) => getFontSize(props.size, false)};
    }

    text-decoration: none;
    font-weight: 500;
    position: relative;
    text-align: center;
    border: none;
    cursor: pointer;
    width: ${(props) => (props.fullWidth ? "100%" : "auto")};
    border: ${(props) => props.inverted
    ? "1px solid" + getMainColor(props.variant, props.theme.colors)
    : "transparent"};
    box-sizing: border-box;
    &:hover {
      background-color: ${(props) => props.inverted
    ? "transparent"
    : getHoverColor(props.variant, props.theme.colors)};
      color: ${(props) => props.inverted
    ? getHoverColor(props.variant, props.theme.colors)
    : getContrastColor(props.variant, props.theme.colors)};
      border: ${(props) => props.inverted
    ? "1px solid" + getHoverColor(props.variant, props.theme.colors)
    : "transparent"};
    }

    &:disabled {
      background-color: ${(props) => props.inverted ? "transparent" : getDisabledColor(props)};
      opacity: ${(props) => (props.inverted ? 0.6 : 1)};
      cursor: default;
    }
  `}
`;
const Button = React.forwardRef(function ButtonInner(props, ref) {
    const { variant = "primary", size = "medium", inverted = false, fullWidth = false, component, href } = props, rest = __rest(props, ["variant", "size", "inverted", "fullWidth", "component", "href"]);
    const as = component !== null && component !== void 0 ? component : (href ? "a" : "button");
    return (React.createElement(StyledButton, Object.assign({ ref: ref, as: as }, (href ? { href } : {}), { variant: variant, size: size, inverted: inverted, fullWidth: fullWidth }, rest)));
});
export default Button;
